/**
 * Mapbox Directions API Integration
 * Fetches driving routes via backend proxy (keeps API key secure)
 */

import { API_BASE_URL } from '../config';

export interface RouteResult {
  distance: number; // in kilometers
  duration: number; // in minutes
  geometry: [number, number][]; // array of [lng, lat] coordinates
}

/**
 * Fetch driving route via backend Mapbox proxy
 * @param fromCoords [lng, lat] coordinates of starting point
 * @param toCoords [lng, lat] coordinates of destination
 * @param signal Optional AbortSignal to cancel the request
 * @returns Route result with distance, duration, and geometry
 */
export async function fetchDrivingRoute(
  fromCoords: [number, number],
  toCoords: [number, number],
  signal?: AbortSignal
): Promise<RouteResult> {
  const [fromLng, fromLat] = fromCoords;
  const [toLng, toLat] = toCoords;

  // Use backend proxy instead of calling Mapbox directly
  const url = `${API_BASE_URL}/api/mapbox/directions?fromLng=${fromLng}&fromLat=${fromLat}&toLng=${toLng}&toLat=${toLat}`;

  try {
    const response = await fetch(url, { signal });
    
    if (!response.ok) {
      throw new Error(`Mapbox Directions API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.routes || data.routes.length === 0) {
      throw new Error('No route found');
    }

    const route = data.routes[0];
    
    return {
      distance: route.distance / 1000, // Convert meters to kilometers
      duration: Math.round(route.duration / 60), // Convert seconds to minutes
      geometry: route.geometry.coordinates as [number, number][]
    };
  } catch (error) {
    // Fallback: return straight line if API fails
    return {
      distance: 0,
      duration: 0,
      geometry: [fromCoords, toCoords]
    };
  }
}

